import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Retention modal shown when a user returns from an abandoned Stripe checkout
 * (cancel_url → /?checkout_cancelled=true). The hottest recovery moment in the funnel.
 *
 * Copy adapts to whether the user already used their 7-day trial before
 * (returning subscribers pay $4.99 immediately, no second free trial).
 */
const CheckoutRecoveryModal = ({ open, onClose, onResume, isProcessing, isReturningSubscriber = false }) => {
  const badge = isReturningSubscriber ? 'TU MÚSICA TE ESPERA' : 'TU PRUEBA GRATIS TE ESPERA';
  const subtitle = isReturningSubscriber
    ? 'Estabas a un paso. Reactiva tu suscripción y vuelve a la librería completa:'
    : 'Estabas a un paso. Recuerda que hoy no pagas nada:';
  const bullets = isReturningSubscriber
    ? [
        'Hoy solo $4.99 — sin compromiso',
        'Acceso inmediato a toda la librería',
        'Cancela en 1 click cuando quieras',
      ]
    : [
        'Hoy pagas $0 — 7 días completamente gratis',
        'Acceso inmediato a toda la librería',
        'Cancela en 1 click antes del día 7 y no se te cobra nada',
      ];
  const cta = isReturningSubscriber
    ? 'Reactivar mi suscripción →'
    : 'Retomar mi prueba gratis →';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
          data-testid="checkout-recovery-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ type: 'spring', damping: 22, stiffness: 300 }}
            className="relative w-full max-w-md bg-[#0D0D12] border-2 border-[#00D4FF]/60 rounded-2xl p-8 shadow-2xl shadow-[#00D4FF]/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Glow accent */}
            <div className="absolute -top-px left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent"></div>

            {/* Badge */}
            <div className="flex justify-center mb-5">
              <span
                className="bg-[#00D4FF] text-black text-xs font-bold px-4 py-1.5 rounded-full tracking-wide"
                data-testid="recovery-badge"
              >
                {badge}
              </span>
            </div>

            <h3
              className="text-2xl font-bold text-white text-center mb-2"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              ¿Necesitas pensarlo?
            </h3>
            <p className="text-[#888] text-center text-sm mb-6">{subtitle}</p>

            <ul className="space-y-3 mb-7">
              {bullets.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm">
                  <svg className="w-4 h-4 text-[#00D4FF] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  <span className="text-[#B3B3B3]">{item}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={onResume}
              disabled={isProcessing}
              className="w-full bg-[#00D4FF] text-black font-bold py-4 rounded-xl hover:bg-[#00D4FF]/90 transition-colors disabled:opacity-50 mb-3"
              data-testid="recovery-resume-btn"
            >
              {isProcessing ? 'Procesando...' : cta}
            </button>
            <button
              onClick={onClose}
              className="w-full text-[#666] text-sm py-2 hover:text-[#999] transition-colors"
              data-testid="recovery-close-btn"
            >
              Quizás después
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CheckoutRecoveryModal;
